import {
  useRef,
  useState,
  type CSSProperties,
  type MutableRefObject,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { ImageUp } from 'lucide-react';
import {
  bulletStyle,
  elementBoxStyle,
  normalizeRotation,
  paragraphStyle,
  placeholderStyle,
  runStyle,
  shapeStyle,
  SLIDE_HEIGHT,
  SLIDE_WIDTH,
  slideNumberStyle,
  slideStyle,
  svgDataUri,
  textElementStyle,
  type Paragraph,
  type Slide,
  type SlideElement,
  type TextElement,
  type TrainingDesign,
} from '@allemnii-pulse/shared';
import { EditableText, type FormatShortcut } from './EditableText.js';
import type { TextRange } from './richText.js';

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
  /** Clockwise degrees about the box's centre; unset means upright. */
  rotation?: number;
}

type Handle = 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'nw';
const HANDLES: Handle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
/** Resize cursors around the compass, starting at north, one per 45 degrees. */
const COMPASS_CURSORS = ['ns-resize', 'nesw-resize', 'ew-resize', 'nwse-resize'];
const HANDLE_ANGLE: Record<Handle, number> = {
  n: 0,
  ne: 45,
  e: 90,
  se: 135,
  s: 180,
  sw: 225,
  w: 270,
  nw: 315,
};
/** The cursor for a handle once the element is rotated — the arrow follows the handle round. */
function handleCursor(handle: Handle, rotation: number): string {
  return COMPASS_CURSORS[Math.round((HANDLE_ANGLE[handle] + rotation) / 45) % 4]!;
}
/** Shift while rotating snaps to this many degrees. */
const ROTATION_SNAP = 15;
/** How far above the box the rotation handle sits, in screen pixels. */
const ROTATE_HANDLE_OFFSET = 28;
const MIN_SIZE = 20;
/** Screen pixels a pointer must travel before a press becomes a drag (a click just selects). */
const DRAG_THRESHOLD = 3;
/** The same for a finger, which wobbles more than a mouse — and on a phone-sized canvas a few
 * screen pixels are dozens of slide pixels, so a tap mustn't nudge the element. */
const TOUCH_DRAG_THRESHOLD = 8;
/** Handle size in screen pixels for a mouse, and for a finger. */
const HANDLE_SIZE = 12;
const TOUCH_HANDLE_SIZE = 24;

const css = (style: Record<string, string>) => style as CSSProperties;

/**
 * The resized box for a handle drag of (dx, dy) canvas pixels on a possibly rotated element. The
 * drag is turned into the element's own (unrotated) frame, resized there, and the box's centre
 * moved back out through the rotation — so the edge or corner opposite the handle stays put on
 * screen, whatever the angle.
 */
function resizeRotatedBox(
  start: Box,
  handle: Handle,
  dx: number,
  dy: number,
  keepRatio: boolean,
): Box {
  const rotation = start.rotation ?? 0;
  if (!rotation) return resizeBox(start, handle, dx, dy, keepRatio);
  const rad = (rotation * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const local = resizeBox(
    start,
    handle,
    dx * cos + dy * sin,
    -dx * sin + dy * cos,
    keepRatio,
    false,
  );
  // How far the centre moved in the element's frame, turned back into canvas coordinates.
  const ox = local.x + local.w / 2 - (start.x + start.w / 2);
  const oy = local.y + local.h / 2 - (start.y + start.h / 2);
  const cx = start.x + start.w / 2 + ox * cos - oy * sin;
  const cy = start.y + start.h / 2 + ox * sin + oy * cos;
  return {
    x: Math.round(cx - local.w / 2),
    y: Math.round(cy - local.h / 2),
    w: Math.round(local.w),
    h: Math.round(local.h),
    rotation,
  };
}

/** The resized box for a handle drag of (dx, dy) canvas pixels, in the element's own frame. With
 * `keepRatio` (Shift on a corner handle) the original aspect ratio is kept. */
function resizeBox(
  start: Box,
  handle: Handle,
  dx: number,
  dy: number,
  keepRatio: boolean,
  round = true,
): Box {
  let { x, y, w, h } = start;
  if (handle.includes('e')) w = start.w + dx;
  if (handle.includes('w')) w = start.w - dx;
  if (handle.includes('s')) h = start.h + dy;
  if (handle.includes('n')) h = start.h - dy;
  if (keepRatio && handle.length === 2) {
    const ratio = start.w / start.h;
    if (Math.abs(w / start.w) > Math.abs(h / start.h)) h = w / ratio;
    else w = h * ratio;
  }
  w = Math.max(MIN_SIZE, w);
  h = Math.max(MIN_SIZE, h);
  if (handle.includes('w')) x = start.x + start.w - w;
  if (handle.includes('n')) y = start.y + start.h - h;
  if (!round) return { x, y, w, h, rotation: start.rotation };
  return {
    x: Math.round(x),
    y: Math.round(y),
    w: Math.round(w),
    h: Math.round(h),
    rotation: start.rotation,
  };
}

/** A text element as static React — the same structure renderSlideHtml emits. */
function StaticText({ el, design }: { el: TextElement; design: TrainingDesign }) {
  return (
    // Placed and rotated by its wrapper on the canvas, so drawn here at the origin, upright.
    <div style={css(textElementStyle({ ...el, x: 0, y: 0, rotation: 0 }, design))}>
      {el.paragraphs.map((p: Paragraph, i) => (
        <div key={i} style={css(paragraphStyle(p))}>
          {p.bullet && <span style={css(bulletStyle(design))}>•</span>}
          {p.runs.some((r) => r.text) ? (
            p.runs.map((r, j) => (
              <span key={j} style={css(runStyle(r))}>
                {r.text}
              </span>
            ))
          ) : (
            <br />
          )}
        </div>
      ))}
    </div>
  );
}

export function SlideCanvas({
  slide,
  design,
  imageUrls,
  width,
  slideNumber,
  selectedId,
  editingId,
  editPoint,
  selectionRef,
  onSelect,
  onStartEditing,
  onStopEditing,
  onCommitBox,
  onParagraphs,
  onTextSelection,
  onRequestUpload,
  onUndo,
  onRedo,
  onFormatShortcut,
}: {
  slide: Slide;
  design: TrainingDesign;
  imageUrls: Record<string, string>;
  /** Display width in screen pixels; the 1920x1080 canvas is scaled to it. */
  width: number;
  /** The slide's 1-based position, shown (not editable) if the slide has showNumber. */
  slideNumber: number;
  selectedId: string | null;
  editingId: string | null;
  editPoint: { x: number; y: number } | null;
  selectionRef: MutableRefObject<TextRange | null>;
  onSelect: (id: string | null) => void;
  onStartEditing: (id: string, point: { x: number; y: number }) => void;
  onStopEditing: () => void;
  onCommitBox: (id: string, box: Box) => void;
  onParagraphs: (id: string, paragraphs: Paragraph[], coalesce: boolean) => void;
  onTextSelection: (range: TextRange | null) => void;
  onRequestUpload: (elementId: string) => void;
  onUndo: () => void;
  onRedo: () => void;
  onFormatShortcut: (kind: FormatShortcut) => void;
}) {
  const scale = width / SLIDE_WIDTH;
  const canvasRef = useRef<HTMLDivElement>(null);
  /** The box of the element being dragged or resized, shown live and committed on release. */
  const [live, setLive] = useState<{ id: string; box: Box } | null>(null);
  // Read once: a device doesn't swap its primary pointer mid-edit.
  const [coarse] = useState(() => window.matchMedia('(pointer: coarse)').matches);

  function startGesture(e: ReactPointerEvent, el: SlideElement, handle: Handle | null) {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    canvasRef.current?.focus({ preventScroll: true });
    if (editingId && editingId !== el.id) onStopEditing();
    // Tapping a text element that's already selected starts editing it — the touch stand-in for
    // the double-click, which phones don't reliably send.
    const touch = e.pointerType === 'touch';
    const tapToEdit = touch && !handle && el.type === 'text' && selectedId === el.id;
    const threshold = touch ? TOUCH_DRAG_THRESHOLD : DRAG_THRESHOLD;
    onSelect(el.id);
    const start: Box = { x: el.x, y: el.y, w: el.w, h: el.h, rotation: el.rotation };
    const origin = { x: e.clientX, y: e.clientY };
    let moved = false;
    let latest = start;

    function onMove(ev: PointerEvent) {
      const sx = ev.clientX - origin.x;
      const sy = ev.clientY - origin.y;
      if (!moved && Math.abs(sx) < threshold && Math.abs(sy) < threshold) return;
      moved = true;
      const dx = sx / scale;
      const dy = sy / scale;
      latest = handle
        ? resizeRotatedBox(start, handle, dx, dy, ev.shiftKey)
        : { ...start, x: Math.round(start.x + dx), y: Math.round(start.y + dy) };
      setLive({ id: el.id, box: latest });
    }
    function stop() {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', stop);
      setLive(null);
    }
    function onUp(ev: PointerEvent) {
      stop();
      if (moved) onCommitBox(el.id, latest);
      else if (tapToEdit) onStartEditing(el.id, { x: ev.clientX, y: ev.clientY });
    }
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    // The browser took the touch over (it became a scroll): drop the gesture, commit nothing.
    window.addEventListener('pointercancel', stop);
  }

  /** Drag on the rotation handle: the element turns to face the pointer, about its centre. Shift
   * snaps to ROTATION_SNAP degrees. One undo step, committed on release. */
  function startRotate(e: ReactPointerEvent, el: SlideElement) {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const start: Box = { x: el.x, y: el.y, w: el.w, h: el.h, rotation: el.rotation };
    // The centre in screen pixels — the canvas is only scaled, never rotated, so this is exact.
    const cx = rect.left + (el.x + el.w / 2) * scale;
    const cy = rect.top + (el.y + el.h / 2) * scale;
    let latest = start;
    let moved = false;

    function onMove(ev: PointerEvent) {
      moved = true;
      // atan2 measures from the positive x axis; the handle sits straight up, at 0 degrees.
      let angle = (Math.atan2(ev.clientY - cy, ev.clientX - cx) * 180) / Math.PI + 90;
      if (ev.shiftKey) angle = Math.round(angle / ROTATION_SNAP) * ROTATION_SNAP;
      latest = { ...start, rotation: normalizeRotation(angle) };
      setLive({ id: el.id, box: latest });
    }
    function stop() {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', stop);
      setLive(null);
    }
    function onUp() {
      stop();
      if (moved) onCommitBox(el.id, latest);
    }
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', stop);
  }

  function renderContent(el: SlideElement) {
    switch (el.type) {
      case 'text':
        return editingId === el.id ? (
          <EditableText
            element={el}
            design={design}
            selectionRef={selectionRef}
            initialPoint={editPoint}
            onParagraphs={(paragraphs, coalesce) => onParagraphs(el.id, paragraphs, coalesce)}
            onSelection={onTextSelection}
            onExit={onStopEditing}
            onUndo={onUndo}
            onRedo={onRedo}
            onFormatShortcut={onFormatShortcut}
          />
        ) : (
          <StaticText el={el} design={design} />
        );
      case 'image': {
        const url = el.storagePath ? imageUrls[el.storagePath] : undefined;
        if (url) {
          return (
            <img
              src={url}
              alt=""
              draggable={false}
              style={{ width: '100%', height: '100%', objectFit: el.fit, display: 'block' }}
            />
          );
        }
        return (
          <div style={{ ...css(placeholderStyle(design)), flexDirection: 'column', gap: 24 }}>
            <span>{el.storagePath ? 'Loading image…' : (el.placeholder ?? 'Image')}</span>
            {!el.storagePath && (
              <button
                type="button"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => onRequestUpload(el.id)}
                className="inline-flex items-center rounded-xl bg-accent text-accent-fg not-italic shadow"
                style={{ fontSize: 30, padding: '14px 28px', gap: 12, opacity: 1 }}
              >
                <ImageUp style={{ width: 34, height: 34 }} />
                Upload image
              </button>
            )}
          </div>
        );
      }
      case 'diagram':
        return (
          <img
            src={svgDataUri(el.svg)}
            alt={el.description}
            draggable={false}
            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
          />
        );
      case 'shape':
        return <div style={css(shapeStyle({ ...el, x: 0, y: 0, rotation: 0 }))} />;
    }
  }

  const selected = slide.elements.find((el) => el.id === selectedId) ?? null;
  const selectedBox = selected ? (live?.id === selected.id ? live.box : selected) : null;
  const selectedRotation = selectedBox?.rotation ?? 0;
  const handleSize = (coarse ? TOUCH_HANDLE_SIZE : HANDLE_SIZE) / scale;
  const outline = 2 / scale;
  const rotateOffset = ROTATE_HANDLE_OFFSET / scale;

  return (
    <div
      className="relative overflow-hidden rounded-md border border-border shadow-sm"
      style={{ width, height: SLIDE_HEIGHT * scale }}
    >
      <div
        ref={canvasRef}
        tabIndex={0}
        className="outline-none"
        style={{
          ...css(slideStyle(slide)),
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
        onPointerDown={() => {
          canvasRef.current?.focus({ preventScroll: true });
          if (editingId) onStopEditing();
          onSelect(null);
        }}
      >
        {slide.elements.map((el) => {
          const box = live?.id === el.id ? live.box : el;
          const editing = editingId === el.id;
          return (
            <div
              key={el.id}
              style={{
                ...css(elementBoxStyle({ ...el, ...box })),
                cursor: editing ? 'text' : 'move',
                // Lines are drawn through the middle of their box; keep the whole box grabbable.
                ...(el.type === 'shape' && el.shape === 'line' ? { minHeight: 24 } : {}),
                // A finger on the selected element drags it; anywhere else it still scrolls the
                // page, so a swipe across the slide can't move what it happens to start on.
                ...(el.id === selectedId && !editing ? { touchAction: 'none' } : {}),
                // No long-press text selection or callout fighting the drag on a touch screen.
                ...(coarse && !editing ? { userSelect: 'none', WebkitTouchCallout: 'none' } : {}),
              }}
              onPointerDown={(e) => {
                if (editing) {
                  e.stopPropagation();
                  return;
                }
                startGesture(e, el, null);
              }}
              onDoubleClick={(e) => {
                e.stopPropagation();
                if (el.type === 'text') onStartEditing(el.id, { x: e.clientX, y: e.clientY });
                else if (el.type === 'image' && !el.storagePath) onRequestUpload(el.id);
              }}
            >
              {renderContent(el)}
            </div>
          );
        })}

        {slide.showNumber && (
          <div className="pointer-events-none" style={css(slideNumberStyle(design))}>
            {slideNumber}
          </div>
        )}

        {selected && selectedBox && (
          <div
            className="pointer-events-none absolute"
            style={{
              left: selectedBox.x,
              top: selectedBox.y,
              width: selectedBox.w,
              height: selectedBox.h,
              outline: `${outline}px solid var(--accent)`,
              outlineOffset: outline,
              // Turned with the element, so the handles sit on its rotated corners and edges.
              ...(selectedRotation ? { transform: `rotate(${selectedRotation}deg)` } : {}),
            }}
          >
            {editingId !== selected.id && (
              <>
                {/* The stalk and the round rotation handle above the top edge. */}
                <div
                  className="absolute left-1/2"
                  style={{
                    top: -rotateOffset,
                    height: rotateOffset,
                    width: outline,
                    marginLeft: -outline / 2,
                    background: 'var(--accent)',
                  }}
                />
                <div
                  title="Rotate (hold Shift to snap to 15°)"
                  className="pointer-events-auto absolute left-1/2 rounded-full border bg-white"
                  style={{
                    top: -rotateOffset - handleSize * 1.3,
                    width: handleSize * 1.3,
                    height: handleSize * 1.3,
                    marginLeft: (-handleSize * 1.3) / 2,
                    borderColor: 'var(--accent)',
                    borderWidth: outline,
                    cursor: 'grab',
                    touchAction: 'none',
                  }}
                  onPointerDown={(e) => startRotate(e, selected)}
                />
              </>
            )}
            {editingId !== selected.id &&
              HANDLES.map((handle) => {
                const left = handle.includes('w') ? 0 : handle.includes('e') ? 100 : 50;
                const top = handle.includes('n') ? 0 : handle.includes('s') ? 100 : 50;
                return (
                  <div
                    key={handle}
                    className="pointer-events-auto absolute rounded-sm border bg-white"
                    style={{
                      left: `${left}%`,
                      top: `${top}%`,
                      width: handleSize,
                      height: handleSize,
                      marginLeft: -handleSize / 2,
                      marginTop: -handleSize / 2,
                      borderColor: 'var(--accent)',
                      borderWidth: outline,
                      cursor: handleCursor(handle, selectedRotation),
                      touchAction: 'none',
                    }}
                    onPointerDown={(e) => startGesture(e, selected, handle)}
                  />
                );
              })}
          </div>
        )}
      </div>
    </div>
  );
}
